import React, { useState, useEffect, useMemo } from "react";
import { X } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Dialog, DialogContent, DialogTrigger } from "../components/ui/dialog";
import { supabase } from "../lib/supabase";

const isVideoFile = (name) => /\.(mp4|webm|mov|m4v)$/i.test(name);

export default function GalleryPage() {
	const [galleryImages, setGalleryImages] = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchImages = async () => {
			const { data, error } = await supabase.storage
				.from("images")
				.list("gallery", {
					limit: 200,
					sortBy: { column: "name", order: "asc" },
				});

			if (error) {
				console.error("Erreur chargement galerie :", error);
				setLoading(false);
				return;
			}

			const images = (data ?? [])
				.filter((f) => f.name && !f.name.startsWith("."))
				.map((file) => {
					const { data: urlData } = supabase.storage
						.from("images")
						.getPublicUrl(`gallery/${file.name}`);
					return {
						id: file.id ?? file.name,
						src: urlData.publicUrl,
						alt: "Ambiance Léger's Coffee",
						type: isVideoFile(file.name) ? "video" : "image",
					};
				});

			setGalleryImages(images);
			setLoading(false);
		};

		fetchImages();
	}, []);

	const pageTitle = "Galerie | Léger's Coffee Genève";
	const pageDescription =
		"Découvrez la galerie photo de Léger's Coffee à Genève : ambiance du coffee shop, cafés de spécialité, pâtisseries artisanales et moments gourmands.";
	const canonicalUrl = "https://legerscoffee.fr/galerie";
	const ogImage =
		galleryImages[0]?.src || "https://legerscoffee.fr/og-image.jpg";

	const structuredDataCollectionPage = {
		"@context": "https://schema.org",
		"@type": "CollectionPage",
		name: "Galerie | Léger's Coffee",
		url: canonicalUrl,
		description: pageDescription,
		inLanguage: "fr-CH",
	};

	const structuredDataImageGallery = useMemo(
		() => ({
			"@context": "https://schema.org",
			"@type": "ImageGallery",
			name: "Galerie photos Léger's Coffee",
			url: canonicalUrl,
			description: pageDescription,
			associatedMedia: galleryImages.map((image) => ({
				"@type": "ImageObject",
				contentUrl: image.src,
				description: image.alt,
			})),
		}),
		[galleryImages],
	);

	return (
		<>
			<Helmet>
				<title>{pageTitle}</title>
				<meta name="description" content={pageDescription} />
				<meta name="robots" content="index, follow" />
				<link rel="canonical" href={canonicalUrl} />

				<meta property="og:type" content="website" />
				<meta property="og:site_name" content="Léger's Coffee" />
				<meta property="og:locale" content="fr_CH" />
				<meta property="og:title" content={pageTitle} />
				<meta property="og:description" content={pageDescription} />
				<meta property="og:url" content={canonicalUrl} />
				<meta property="og:image" content={ogImage} />

				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={pageTitle} />
				<meta name="twitter:description" content={pageDescription} />
				<meta name="twitter:image" content={ogImage} />

				<script type="application/ld+json">
					{JSON.stringify(structuredDataCollectionPage)}
				</script>
				<script type="application/ld+json">
					{JSON.stringify(structuredDataImageGallery)}
				</script>
			</Helmet>

			<main data-testid="gallery-page" className="min-h-screen pt-20">
				{/* Hero */}
				<section className="bg-warm-secondary py-16 lg:py-24">
					<div className="max-w-7xl mx-auto px-6 lg:px-12">
						<div className="text-center max-w-2xl mx-auto">
							<span className="text-latte-gold text-sm uppercase tracking-widest font-medium mb-4 block">
								Notre Univers
							</span>
							<h1 className="font-serif text-4xl lg:text-5xl text-espresso font-semibold mb-6">
								Galerie
							</h1>
							<p className="text-soft-charcoal-secondary">
								Découvrez l'atmosphère chaleureuse de Léger's Coffee à travers
								notre galerie photos.
							</p>
						</div>
					</div>
				</section>

				{/* Gallery Grid */}
				<section className="py-16 lg:py-24 bg-warm-alabaster">
					<div className="max-w-7xl mx-auto px-6 lg:px-12">
						{loading ? (
							<p className="text-center text-soft-charcoal-secondary py-12">
								Chargement de la galerie…
							</p>
						) : galleryImages.length === 0 ? (
							<p className="text-center text-soft-charcoal-secondary py-12">
								Aucune image disponible pour le moment.
							</p>
						) : (
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
								{galleryImages.map((image, index) => (
									<Dialog key={image.id}>
										<DialogTrigger asChild>
											<button
												type="button"
												className="group relative aspect-square overflow-hidden bg-warm-secondary cursor-pointer opacity-0 animate-fade-in"
												style={{
													animationDelay: `${(index % 6) * 0.1}s`,
												}}
												data-testid={`gallery-image-${image.id}`}
											>
												{image.type === "video" ? (
													<video
														src={image.src}
														muted
														loop
														playsInline
														autoPlay
														className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
													/>
												) : (
													<img
														src={image.src}
														alt={image.alt}
														loading="lazy"
														className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
													/>
												)}
												<div className="absolute inset-0 bg-espresso/0 group-hover:bg-espresso/20 transition-colors duration-300" />
												<div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-espresso/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
													<p className="text-warm-alabaster text-sm font-medium">
														{image.alt}
													</p>
												</div>
											</button>
										</DialogTrigger>
										<DialogContent className="max-w-4xl p-0 bg-transparent border-none">
											{image.type === "video" ? (
												<video
													src={image.src}
													controls
													autoPlay
													className="w-full h-auto max-h-[80vh]"
												/>
											) : (
												<img
													src={image.src}
													alt={image.alt}
													className="w-full h-auto max-h-[80vh] object-contain"
												/>
											)}
										</DialogContent>
									</Dialog>
								))}
							</div>
						)}
					</div>
				</section>
			</main>
		</>
	);
}
